/**
 * CAB API Service
 *
 * REST API client for the Common Application Blueprint module.
 */

import axios from 'axios';
import api from '../utils/axios';
import { filenameFromContentDisposition } from '../utils/fileUtils';
import type {
  CABFileUploadResponse,
  CABFileListResponse,
  CABFileInfoResponse,
  CABSessionResponse,
  CABProgressResponse,
  CABGenerateRequest,
  PatchRequest,
  PatchResult,
  CABVersionResponse,
  EditReviewRequest,
  EditReviewResponse,
  EditReviewJobStartResponse,
  EditReviewJobStatusResponse,
  ApplyReviewRequest,
  ApplyReviewResponse,
  ChangeDecisionInfo,
  ReviewDecisionsResponse,
  CabChatMessage,
  CabChatMessageListResponse,
  SendCabChatMessageRequest,
  CabChatSendResponse,
  CabChatLockStatusResponse,
} from '../types/cab';

const CAB_BASE = '/cab';

// ════════════════════════════════════════════════════════════════════════════
// FILE MANAGEMENT
// ════════════════════════════════════════════════════════════════════════════

/**
 * Upload a file for CAB processing.
 * Supports large files — streamed as multipart/form-data.
 */
export async function uploadCABFile(
  projectId: string,
  file: File,
  onProgress?: (percent: number) => void,
  abortController?: AbortController,
): Promise<CABFileUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<CABFileUploadResponse>(
    `${CAB_BASE}/${projectId}/files/upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: abortController?.signal,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    },
  );
  return response.data;
}

/**
 * List all uploaded CAB files for a project.
 */
export async function listCABFiles(projectId: string): Promise<CABFileListResponse> {
  const response = await api.get<CABFileListResponse>(
    `${CAB_BASE}/${projectId}/files`,
  );
  return response.data;
}

/**
 * Delete a CAB file.
 */
export async function deleteCABFile(projectId: string, fileId: string): Promise<void> {
  await api.delete(`${CAB_BASE}/${projectId}/files/${fileId}`);
}

/**
 * Download an uploaded CAB file.
 */
export async function downloadCABFile(projectId: string, fileId: string, fileName: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/files/${fileId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════════════
// SESSION STATUS & PROGRESS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Lightweight check for CAB.md availability — used by DocumentsPanel / FileUploadDialog.
 */
export async function getCABFileInfo(projectId: string): Promise<CABFileInfoResponse> {
  const response = await api.get<CABFileInfoResponse>(
    `${CAB_BASE}/${projectId}/file-info`,
  );
  return response.data;
}

/**
 * Get CAB session status for a project.
 */
export async function getCABStatus(projectId: string): Promise<CABSessionResponse> {
  const response = await api.get<CABSessionResponse>(
    `${CAB_BASE}/${projectId}/status`,
  );
  return response.data;
}

/**
 * Get detailed CAB generation progress (for polling).
 */
export async function getCABProgress(projectId: string): Promise<CABProgressResponse> {
  const response = await api.get<CABProgressResponse>(
    `${CAB_BASE}/${projectId}/progress`,
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// GENERATION CONTROL (placeholder — wired in Stage 2)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Start CAB generation.
 */
export async function generateCAB(
  projectId: string,
  config?: CABGenerateRequest,
): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/generate`, config || {});
}

/**
 * Pause CAB generation.
 */
export async function pauseCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/pause`);
}

/**
 * Resume CAB generation from checkpoint.
 */
export async function resumeCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/resume`);
}

/**
 * Cancel CAB generation.
 */
export async function cancelCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/cancel`);
}

/**
 * Regenerate CAB from scratch.
 */
export async function regenerateCAB(
  projectId: string,
  config?: CABGenerateRequest,
): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/regenerate`, config || {});
}

// ════════════════════════════════════════════════════════════════════════════
// OUTPUT ACCESS (placeholder — wired in Stage 4-5)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Get the skeleton JSON.
 */
export async function getCABSkeleton(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/skeleton`);
  return response.data;
}

/**
 * Get the final CAB JSON.
 */
export async function getCABJson(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/cab`);
  return response.data;
}

/**
 * Get the rendered HTML (runtime from CAB JSON + template).
 */
export async function getCABHtml(projectId: string, wip = false, cabSessionId?: string): Promise<string> {
  const response = await api.get(`${CAB_BASE}/${projectId}/html`, {
    params: { wip, ...(cabSessionId ? { cab_session_id: cabSessionId } : {}) },
    responseType: 'text',
  });
  return response.data;
}

/**
 * Fetch the CAB markdown content as text (for preview).
 */
export async function getCABMarkdownContent(projectId: string): Promise<string> {
  const response = await api.get(`${CAB_BASE}/${projectId}/markdown`, {
    responseType: 'text',
  });
  return response.data;
}

/**
 * Download the CAB as a Markdown file.
 */
export async function downloadCABMarkdown(projectId: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/markdown`, {
    responseType: 'blob',
  });

  const filename = filenameFromContentDisposition(response.headers['content-disposition'], 'CAB.md');

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Download the CAB as a PDF file.
 */
export async function downloadCABPdf(projectId: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/pdf`, {
    responseType: 'blob',
  });

  const filename = filenameFromContentDisposition(response.headers['content-disposition'], 'CAB.pdf');

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Update CAB share settings (enable/disable public link)
 */
export async function updateCABShareSettings(
  projectId: string,
  enabled: boolean
): Promise<{ readonly_sharable_link_enabled: boolean }> {
  const response = await api.put(`${CAB_BASE}/${projectId}/share`, {
    readonly_sharable_link_enabled: enabled,
  });
  return response.data;
}

/**
 * Get an individual section JSON.
 */
export async function getCABSection(projectId: string, sectionName: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/section/${sectionName}`);
  return response.data;
}

/**
 * Get cost breakdown.
 */
export async function getCABCost(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/cost`);
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// DELETE
// ════════════════════════════════════════════════════════════════════════════

/**
 * Delete all CAB data for a project.
 */
export async function deleteCAB(projectId: string): Promise<void> {
  await api.delete(`${CAB_BASE}/${projectId}`);
}

// ════════════════════════════════════════════════════════════════════════════
// INLINE EDIT (PATCH)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Get the current CAB version for optimistic concurrency.
 */
export async function getCABVersion(projectId: string): Promise<CABVersionResponse> {
  const response = await api.get<CABVersionResponse>(`${CAB_BASE}/${projectId}/cab/version`);
  return response.data;
}

/**
 * Get the rendered HTML with edit mode enabled.
 */
export async function getCABHtmlEditable(projectId: string): Promise<string> {
  const response = await api.get(`${CAB_BASE}/${projectId}/html`, {
    params: { editable: true },
    responseType: 'text',
  });
  return response.data;
}

/**
 * Apply patch operations to the CAB document.
 */
export async function patchCAB(
  projectId: string,
  patch: PatchRequest
): Promise<PatchResult> {
  const response = await api.patch<PatchResult>(`${CAB_BASE}/${projectId}/cab`, {
    base_version: patch.baseVersion,
    ops: patch.ops,
  });
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// CAB AI REVIEW — shared by Validate-with-AI and AI Chat
// ════════════════════════════════════════════════════════════════════════════

/** Max wall-clock time to poll for a background review job (server may run longer). */
const CAB_REVIEW_CLIENT_POLL_MAX_MS = 30 * 60 * 1000;

/** How long status polls may keep failing (backend restart, network drop) before giving up. */
const CAB_REVIEW_POLL_FAILURE_GRACE_MS = 2 * 60 * 1000;

/** Polling gave up on a job that may still finish on the server. */
export class ReviewPollConnectionError extends Error {
  constructor() {
    super('Lost connection while the AI job was running. It may still finish — reload the page to check.');
    this.name = 'ReviewPollConnectionError';
  }
}

function isTransientPollError(err: unknown): boolean {
  return axios.isAxiosError(err) && (!err.response || err.response.status >= 500);
}

const CAB_REVIEW_JOBS_PATH = (projectId: string) =>
  `${CAB_BASE}/${projectId}/cab/review-edits/jobs`;

/**
 * Fetch the latest active (queued/running) or most-recently-completed review
 * job for a project. Returns null when no such job exists (HTTP 204).
 *
 * Used on page mount so users can resume a poll or hydrate a completed review
 * after a tab refresh.
 */
export async function getActiveReviewJob(
  projectId: string,
): Promise<EditReviewJobStatusResponse | null> {
  const resp = await api.get<EditReviewJobStatusResponse>(
    `${CAB_BASE}/${projectId}/cab/review-edits/jobs/active`,
    { validateStatus: (s) => s === 200 || s === 204 },
  );
  return resp.status === 204 ? null : resp.data;
}

/**
 * Request server-side cancellation of a running or queued review job.
 * The background task will transition to ``cancelled`` before its LLM call.
 */
export async function cancelReviewJob(
  projectId: string,
  jobId: string,
): Promise<void> {
  await api.delete(
    `${CAB_BASE}/${projectId}/cab/review-edits/jobs/${encodeURIComponent(jobId)}`,
  );
}

/**
 * Poll an existing review job by job_id until it reaches a terminal state.
 *
 * Extracted from ``reviewCABEdits`` so it can be reused by the mount-time
 * resume flow without needing to POST a new job.
 */
export async function pollReviewJob(
  projectId: string,
  jobId: string,
  pollIntervalSec: number,
  options?: { signal?: AbortSignal },
): Promise<EditReviewResponse> {
  const intervalMs = Math.max(1, pollIntervalSec) * 1000;
  const deadline = Date.now() + CAB_REVIEW_CLIENT_POLL_MAX_MS;
  let failingSince: number | null = null;

  while (Date.now() < deadline) {
    if (options?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    let st: EditReviewJobStatusResponse | null = null;
    try {
      const resp = await api.get<EditReviewJobStatusResponse>(
        `${CAB_BASE}/${projectId}/cab/review-edits/jobs/${encodeURIComponent(jobId)}`,
        { timeout: 120_000, signal: options?.signal },
      );
      st = resp.data;
      failingSince = null;
    } catch (err) {
      if (options?.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      if (!isTransientPollError(err)) {
        throw err;
      }
      if (failingSince === null) {
        failingSince = Date.now();
        console.warn(`Review job ${jobId} status poll failed, retrying`, err);
      } else if (Date.now() - failingSince > CAB_REVIEW_POLL_FAILURE_GRACE_MS) {
        throw new ReviewPollConnectionError();
      }
    }
    if (st?.status === 'completed' && st.result) {
      return st.result;
    }
    if (st?.status === 'failed') {
      throw new Error(st.error || 'AI review failed');
    }
    if (st?.status === 'cancelled') {
      throw new DOMException('Review was cancelled', 'AbortError');
    }
    await new Promise<void>((resolve, reject) => {
      const id = window.setTimeout(resolve, intervalMs);
      if (options?.signal) {
        options.signal.addEventListener(
          'abort',
          () => {
            window.clearTimeout(id);
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true },
        );
      }
    });
  }
  throw new Error('AI review timed out while waiting for results');
}

/**
 * Enqueue a CAB AI review job (Validate-with-AI or chat-style prompt body).
 * Poll with ``pollReviewJob`` until completed.
 */
export async function startCABReviewJob(
  projectId: string,
  request: EditReviewRequest,
  options?: { signal?: AbortSignal },
): Promise<EditReviewJobStartResponse> {
  const { data } = await api.post<EditReviewJobStartResponse>(
    CAB_REVIEW_JOBS_PATH(projectId),
    request,
    { timeout: 120_000, signal: options?.signal },
  );
  return data;
}

/**
 * Enqueue a review job and poll until it completes (shared by edit + chat UIs).
 */
export async function runCABReview(
  projectId: string,
  request: EditReviewRequest,
  options?: { signal?: AbortSignal },
): Promise<{ result: EditReviewResponse; jobId: string }> {
  const start = await startCABReviewJob(projectId, request, options);
  const result = await pollReviewJob(
    projectId,
    start.job_id,
    start.poll_interval_sec,
    options,
  );
  return { result, jobId: start.job_id };
}

/**
 * Validate-with-AI: enqueue + poll (alias for ``runCABReview``).
 */
export async function reviewCABEdits(
  projectId: string,
  request: EditReviewRequest,
  options?: { signal?: AbortSignal },
): Promise<{ result: EditReviewResponse; jobId: string }> {
  return runCABReview(projectId, request, options);
}

/**
 * Persist whichever review outcome the user picked from the four-button
 * review screen (save_ai / save_manual_only / discard).
 *
 * Calling this with an unknown or expired review_id returns a 400 from the
 * backend; the caller should refresh the editor in that case.
 */
export async function applyCABReview(
  projectId: string,
  request: ApplyReviewRequest,
): Promise<ApplyReviewResponse> {
  const response = await api.post<ApplyReviewResponse>(
    `${CAB_BASE}/${projectId}/cab/apply-review`,
    request,
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// PER-CHANGE DECISIONS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Record (or clear) a per-change accept/reject decision.
 *
 * Last write wins — any reviewer can overwrite any other reviewer's decision.
 * Pass `decision: null` to clear a previously recorded decision (back to pending).
 *
 * Fire-and-forget safe: the local UI state is already optimistically updated
 * before this is called, so a network error here only means the decision
 * isn't synced to other reviewers until their next refresh.
 */
export async function recordChangeDecision(
  projectId: string,
  reviewId: string,
  changeKey: string,
  decision: 'accepted' | 'rejected' | null,
): Promise<ChangeDecisionInfo | null> {
  const response = await api.post<ChangeDecisionInfo | null>(
    `${CAB_BASE}/${projectId}/cab/review-edits/${encodeURIComponent(reviewId)}/decisions`,
    { change_key: changeKey, decision },
  );
  return response.data ?? null;
}

/**
 * Fetch the current shared decision state for a review.
 *
 * Called on page load / review hydration so every reviewer sees the same
 * accept/reject status that was recorded before the refresh.
 */
export async function getReviewDecisions(
  projectId: string,
  reviewId: string,
): Promise<ReviewDecisionsResponse> {
  const response = await api.get<ReviewDecisionsResponse>(
    `${CAB_BASE}/${projectId}/cab/review-edits/${encodeURIComponent(reviewId)}/decisions`,
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY
// ════════════════════════════════════════════════════════════════════════════

export interface CABVersion {
  filename: string;
  timestamp_ms: number;
  size: number;
  /** Position in the history: Version 1 is the oldest. */
  number: number;
  /** When this content became the current CAB. */
  became_current_ms: number;
}

/** The current CAB's place in the version history. */
export interface CABCurrentVersion {
  /** One more than the number of earlier versions. */
  number: number;
  became_current_ms: number;
  /** The current CAB was brought back by a restore (its content matches an earlier saved CAB). */
  restored: boolean;
}

export interface CABVersionList {
  /** The earlier versions, newest first. Versions a restore removed are left out. */
  versions: CABVersion[];
  /** Null when there is no current CAB. */
  current: CABCurrentVersion | null;
  /** The project syncs the CAB to its tickets and has tickets, so a restore can leave ticket updates to review. */
  ticket_review: boolean;
}

export interface CABRestoreResult {
  success: boolean;
  restored_timestamp_ms: number;
  /** The version files this request deleted. */
  deleted_versions: string[];
  new_version: string;
  /**
   * Timestamp of the snapshot of the CAB the restore replaced (null when there was none). The restore undoes it,
   * so it is not listed and may already be deleted (see deleted_versions).
   */
  saved_current_as?: number | null;
  /** The version already matched the current CAB, so nothing was written. */
  unchanged?: boolean;
}

/**
 * List the CAB version history for a project, newest first, with the
 * current CAB's place in that history.
 */
export async function listCABVersions(projectId: string): Promise<CABVersionList> {
  const response = await api.get<Partial<CABVersionList>>(
    `${CAB_BASE}/${projectId}/cab/versions`,
  );
  const data = response.data;
  return {
    versions: data.versions || [],
    current: data.current ?? null,
    ticket_review: data.ticket_review ?? false,
  };
}

/**
 * Get the rendered HTML for a specific CAB version snapshot.
 */
export async function getCABVersionHtml(
  projectId: string,
  timestampMs: number,
): Promise<string> {
  const response = await api.get(
    `${CAB_BASE}/${projectId}/cab/versions/${timestampMs}/html`,
    { responseType: 'text' },
  );
  return response.data;
}

/**
 * Restore CAB to a specific version snapshot: it becomes the current CAB again
 * and the versions after it leave the history; their files are deleted.
 * A version that already matches the current CAB changes nothing (`unchanged`).
 */
export async function restoreCABVersion(
  projectId: string,
  timestampMs: number,
): Promise<CABRestoreResult> {
  const response = await api.post<CABRestoreResult>(
    `${CAB_BASE}/${projectId}/cab/versions/${timestampMs}/restore`,
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// CAB CHAT
// ════════════════════════════════════════════════════════════════════════════

/**
 * Fetch chat messages for a project with cursor-based pagination.
 * Returns messages in chronological order (oldest first).
 *
 * @param projectId - The project ID
 * @param before - Optional cursor for loading older messages
 * @param limit - Number of messages per page (default 20, max 50)
 */
export async function getCabChatMessages(
  projectId: string,
  before?: string,
  limit: number = 10,
): Promise<CabChatMessageListResponse> {
  const params: Record<string, any> = { limit };
  if (before) {
    params.before = before;
  }
  const response = await api.get<CabChatMessageListResponse>(
    `${CAB_BASE}/${projectId}/cab/chat/messages`,
    { params },
  );
  return response.data;
}

/**
 * AI Chat: enqueue via the same ``POST …/review-edits/jobs`` route as Validate-with-AI.
 * Poll the returned ``job_id`` with ``pollReviewJob``.
 */
export async function sendCabChatMessage(
  projectId: string,
  request: SendCabChatMessageRequest,
): Promise<CabChatSendResponse> {
  const { data } = await api.post<EditReviewJobStartResponse>(
    CAB_REVIEW_JOBS_PATH(projectId),
    {
      base_version: request.base_version,
      ops: [],
      prompt: request.content,
      model_preference: request.model_preference,
    } satisfies EditReviewRequest,
  );
  if (!data.message_id) {
    throw new Error('Chat review did not return message_id');
  }
  return {
    message_id: data.message_id,
    job_id: data.job_id,
    poll_interval_sec: data.poll_interval_sec,
    status: data.status,
  };
}

/**
 * Get the current chat lock status for a project.
 * Use this to check if another user has the project locked.
 */
export async function getCabChatLockStatus(
  projectId: string,
): Promise<CabChatLockStatusResponse> {
  const response = await api.get<CabChatLockStatusResponse>(
    `${CAB_BASE}/${projectId}/cab/chat/lock-status`,
  );
  return response.data;
}

/**
 * Release the chat lock after applying or discarding a review.
 * Called automatically by applyCABReview, but can be called manually
 * if the user wants to abandon a pending review.
 */
export async function releaseCabChatLock(projectId: string): Promise<void> {
  await api.delete(`${CAB_BASE}/${projectId}/cab/chat/lock`);
}
